import { createSelector } from 'reselect';
import { readFlows, selectFlow, selectFlows } from 'next/entities/flows';
import { selectPins, selectPin, readPins } from 'next/entities/pins';
import { readRules, selectRule } from 'next/entities/rules';
import { readSegments, selectSegment } from 'next/entities/segments';
import { readTags, selectTag } from 'next/entities/tags';

/**
 * NOTE: Consider using reselect for these compound selectors as well as the
 *       various selectors that use request status.
 *
 *       Also, once we figure out how best to handle error states, we should
 *       handle that in the UI appropriately.
 *
 *       Finally, many of the compound selectors here _could_ be generalized to
 *       handle flows AND experiences and should be renamed appropriately
 */

/**
 * Helper function to determine status of requests
 *
 * @param {...RequestStatus} requests - Request statuses
 * @return {RequestStatus} Consolidated status
 */
const check = (...requests) => {
  const errored = requests.some(({ error }) => error);
  if (errored) {
    return { loading: false, error: true };
  }

  const pending = requests.some(({ loading }) => loading);
  if (pending) {
    return { loading: true, error: false };
  }

  return null;
};

/**
 * Extract property from conditions
 *
 * @param {Conditions} conditions - Flow conditions
 * @param {string} property - Condition property to extract
 * @return {Condition[]} Conditions for a property
 */
const extractConditions = (conditions, property) =>
  (conditions?.and ?? [])
    .flatMap(({ and = [], or = [] }) => [...and, ...or])
    .filter(rule => rule[property])
    .flatMap(rule => rule[property]);

/**
 * Read target domains for a flow
 *
 * @param {State} state - Redux state
 * @param {string} id - Flow ID
 * @return {RequestStatus<string[]>} List of target domains for the flow
 */
export const readDomainsForFlow = (state, id) => {
  const rules = readRules(state);

  const status = check(rules);
  if (status !== null) {
    return status;
  }

  const { conditions } = selectRule(state, id) ?? {};
  const extracted = extractConditions(conditions, 'domains');

  const data = extracted
    .filter(({ operator }) => operator === '==')
    .map(({ value }) => value)
    .filter(Boolean);

  return { data, loading: false, error: false };
};

/**
 * Read segments for a flow
 *
 * @param {State} state - Redux state
 * @param {string} id - Flow ID
 * @return {RequestStatus<Segment[]>} List of segments for the flow
 */
export const readSegmentsForFlow = (state, id) => {
  const rules = readRules(state);
  const segments = readSegments(state);

  const status = check(rules, segments);
  if (status !== null) {
    return status;
  }

  const { conditions } = selectRule(state, id) ?? {};
  const extracted = extractConditions(conditions, 'segments');

  const data = extracted
    .map(({ segment }) => selectSegment(state, segment))
    .filter(Boolean);

  return { data, loading: false, error: false };
};

export const readTagsForExperience = (state, tagIds) => {
  const data = (tagIds ?? []).map(tag => selectTag(state, tag)).filter(Boolean);

  return { data, loading: false, error: false };
};

/**
 * Read tags for a flow
 *
 * @param {State} state - Redux state
 * @param {string} id - Flow ID
 * @return {RequestStatus<Tag[]>} List of tags for the flow
 */
export const readTagsForFlow = (state, id) => {
  const flows = readFlows(state);
  const tags = readTags(state);

  const status = check(flows, tags);
  if (status !== null) {
    return status;
  }

  const { tagIds } = selectFlow(state, id) ?? {};
  return readTagsForExperience(state, tagIds);
};

/**
 * Read tags for a flow
 *
 * @param {State} state - Redux state
 * @param {string} id - Pin ID
 * @return {RequestStatus<Tag[]>} List of tags for the pin
 */
export const readTagsForPins = (state, id) => {
  const pins = readPins(state);
  const tags = readTags(state);

  const status = check(pins, tags);
  if (status !== null) {
    return status;
  }

  const { tagIds } = selectPin(state, id) ?? {};
  return readTagsForExperience(state, tagIds);
};

/**
 * Select associations for tags
 *
 * @param {State} state - Redux state
 * @return {object<string[]>} List of IDs associated with tags
 */
export const selectAssociationsForTags = createSelector(
  selectFlows,
  selectPins,
  (flows = {}, pins = {}) => {
    const experiences = { ...flows, ...pins };

    return Object.values(experiences).reduce((acc, experience) => {
      const tags = experience.tagIds ?? [];

      tags.forEach(id => {
        acc[id] = [...(acc[id] || []), experience.id];
      });

      return acc;
    }, {});
  }
);

/**
 * Select associations for a single tag
 *
 * @param {State} state - Redux state
 * @return {string[]} List of IDs associated for tag
 */
export const selectAssociationsForTag = createSelector(
  selectAssociationsForTags,
  (state, id) => id,
  (associations, id) => associations[id]
);
